import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useParams, useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import UsageLimitComponent from "./component";

const UsageLimitContainer = (props) => {
  const { corporateId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [usageLimitData, setUsageLimitData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    usageLimitRequired: true,
    limitOn: "AMOUNT",
    frequency: "DAYS",
    value: ""
  });

  // Helper function to map API response to UI format
  const mapApiToUi = (apiData) => {
    console.log("Mapping API to UI:", apiData);
    
    // Map frequency to uppercase for UI consistency
    let frequencyUi = apiData.frequency ? apiData.frequency.toUpperCase() : "DAYS";
    
    // Ensure frequency is in correct format for UI
    if (frequencyUi === "HOUR") frequencyUi = "HOURS";
    else if (frequencyUi === "DAY") frequencyUi = "DAYS";
    else if (frequencyUi === "MONTH") frequencyUi = "MONTHS";
    
    // Map limitOn to uppercase
    let limitOnUi = apiData.limitOn ? apiData.limitOn.toUpperCase() : "AMOUNT";
    if (limitOnUi === "TRANSACTION") limitOnUi = "TRANSACTION_COUNT";
    
    return {
      usageLimitRequired: apiData.usageLimitRequired === true || apiData.usageLimitRequired === "true",
      limitOn: limitOnUi,
      frequency: frequencyUi,
      value: apiData.value || 0
    };
  };

  // Helper function to map UI to API format
  const mapUiToApi = (uiData) => {
    console.log("Mapping UI to API:", uiData);
    
    // Map frequency to correct API format
    let frequencyApi = uiData.frequency || "DAYS";
    
    // API expects: "HOURS", "DAYS", or "MONTH" (not MONTHS)
    if (frequencyApi === "HOURS") frequencyApi = "HOURS";
    else if (frequencyApi === "DAYS") frequencyApi = "DAYS";
    else if (frequencyApi === "MONTHS") frequencyApi = "MONTH";
    
    // Map limitOn - API expects "AMOUNT" or "TRANSACTION"
    let limitOnApi = uiData.limitOn || "AMOUNT";
    if (limitOnApi === "TRANSACTION_COUNT") limitOnApi = "TRANSACTION";
    
    return {
      usageLimitRequired: uiData.usageLimitRequired === true || uiData.usageLimitRequired === "true",
      limitOn: limitOnApi,
      frequency: frequencyApi,
      value: uiData.usageLimitRequired ? parseInt(uiData.value) : 0
    };
  };

  useEffect(() => {
    if (corporateId) {
      fetchUsageLimitData();
    }
  }, [corporateId]);

  const fetchUsageLimitData = () => {
    setLoading(true);
    
    props.getDataFromAPI(
      `/partner/api/v2/corporate/${corporateId}/usage-limit`,
      "GET",
      undefined,
      (response) => {
        console.log("GET API Response:", response);
        
        if (response) {
          // Check if response has data or is directly the data
          const apiData = response || response;
          console.log("API Data received:", apiData);
          
          // Map API data to UI format
          const mappedData = mapApiToUi(apiData);
          console.log("Mapped Data for UI:", mappedData);
          
          setUsageLimitData(mappedData);
          setFormData({
            usageLimitRequired: mappedData.usageLimitRequired,
            limitOn: mappedData.limitOn,
            frequency: mappedData.frequency,
            value: mappedData.value.toString()
          });
        } else {
          console.log("No data received, setting defaults");
          const defaultData = {
            usageLimitRequired: false,
            limitOn: "AMOUNT",
            frequency: "DAYS",
            value: 0
          };
          
          setUsageLimitData(defaultData);
          setFormData({
            usageLimitRequired: false,
            limitOn: "AMOUNT",
            frequency: "DAYS",
            value: "0"
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching usage limit data:", error);
        toastr.error("Failed", "Unable to fetch usage limit data");
        
        const errorData = {
          usageLimitRequired: false,
          limitOn: "AMOUNT",
          frequency: "DAYS",
          value: 0
        };
        
        setUsageLimitData(errorData);
        setFormData({
          usageLimitRequired: false,
          limitOn: "AMOUNT",
          frequency: "DAYS",
          value: "0"
        });
        setLoading(false);
      }
    );
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelClick = () => {
    if (usageLimitData) {
      setFormData({
        usageLimitRequired: usageLimitData.usageLimitRequired,
        limitOn: usageLimitData.limitOn,
        frequency: usageLimitData.frequency,
        value: usageLimitData.value.toString()
      });
    }
    setIsEditMode(false);
  };

  const handleInputChange = (field, value) => {
    console.log(`Field: ${field}, Value: ${value}`);
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateLimit = () => {
    console.log("Updating with formData:", formData);
    
    // Validation
    if (formData.usageLimitRequired) {
      if (!formData.value || isNaN(formData.value) || parseFloat(formData.value) <= 0) {
        toastr.error("Error", "Please enter a valid limit value");
        return;
      }
      
      if (!formData.limitOn) {
        toastr.error("Error", "Please select what you want to limit");
        return;
      }
      
      if (!formData.frequency) {
        toastr.error("Error", "Please select a frequency");
        return;
      }
    }

    setLoading(true);
    
    // Map UI data to API format
    const apiPayload = mapUiToApi(formData);
    console.log("PUT API Payload:", apiPayload);

    props.getDataFromAPI(
      `/partner/api/v2/corporate/${corporateId}/usage-limit`,
      "PUT",
      apiPayload,
      (response) => {
        console.log("PUT API Success:", response);
        
        // Update local state with UI format
        const updatedData = {
          usageLimitRequired: formData.usageLimitRequired,
          limitOn: formData.limitOn,
          frequency: formData.frequency,
          value: formData.usageLimitRequired ? parseInt(formData.value) : 0
        };
        
        setUsageLimitData(updatedData);
        toastr.success("Success", "Usage limit updated successfully");
        setLoading(false);
        setIsEditMode(false);
      },
      (error) => {
        console.error("PUT API Error:", error);
        toastr.error("Failed", "Unable to update usage limit");
        setLoading(false);
      }
    );
  };

  return (
    <UsageLimitComponent
      loading={loading}
      usageLimitData={usageLimitData}
      isEditMode={isEditMode}
      formData={formData}
      handleBackClick={handleBackClick}
      handleEditClick={handleEditClick}
      handleCancelClick={handleCancelClick}
      handleInputChange={handleInputChange}
      handleUpdateLimit={handleUpdateLimit}
      corporateId={corporateId}
    />
  );
};

export default connect(null, { getDataFromAPI })(UsageLimitContainer);